import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { KeenIcon } from '@/components/keenicons';
import {
  useAllPermissions,
  useUpdateRolePermissions,
} from '@/hooks/admin/usePermissions';
import type {
  Role,
  RolePermissionsResponse,
  Permission,
  PermissionGroup,
} from '@/services/admin/role.types';
import { toast } from 'sonner';

interface RolePermissionsTabProps {
  role: Role;
  rolePermissions?: RolePermissionsResponse;
  isEditMode: boolean;
}

/**
 * Role Permissions Tab Component
 *
 * Displays and manages permission assignments for a role
 *
 * Features:
 * - Hierarchical permission tree grouped by provider
 * - Select all/none per group
 * - Individual permission selection
 * - Search/filter permissions
 * - Save changes with optimistic updates
 *
 * Spec reference: Lines 300-336 and 447-583 in 03-AUTH-AUTHORIZATION-SPECS.md
 */
export function RolePermissionsTab({
  role,
  rolePermissions,
  isEditMode,
}: RolePermissionsTabProps) {
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const { data: allPermissionsData } = useAllPermissions();
  const updatePermissionsMutation = useUpdateRolePermissions();

  // Initialize selected permissions from role permissions
  useEffect(() => {
    if (rolePermissions) {
      const granted = new Set<string>();
      rolePermissions.groups.forEach((group) => {
        group.permissions.forEach((perm) => {
          if (perm.isGranted) {
            granted.add(perm.name);
          }
        });
      });
      setSelectedPermissions(granted);
    }
  }, [rolePermissions]);

  // Group all permissions by provider
  const permissionGroups = useMemo(() => {
    if (!allPermissionsData) return [];

    const groups = new Map<string, Permission[]>();

    allPermissionsData.permissions.forEach((perm) => {
      const provider = perm.providerName || 'Other';
      if (!groups.has(provider)) {
        groups.set(provider, []);
      }

      // Check if this permission is granted to the role
      const isGranted = selectedPermissions.has(perm.name);

      groups.get(provider)!.push({
        name: perm.name,
        displayName: perm.displayName,
        description: perm.description,
        parentName: perm.parentName,
        isGranted,
        allowedProviders: [],
      });
    });

    // Convert to array and sort
    return Array.from(groups.entries())
      .map(([name, permissions]) => ({
        name,
        displayName: formatProviderName(name),
        permissions: permissions.sort((a, b) => a.displayName.localeCompare(b.displayName)),
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [allPermissionsData, selectedPermissions]);

  // Filter groups by search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return permissionGroups;

    const query = searchQuery.toLowerCase();
    return permissionGroups
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter(
          (p) =>
            p.name.toLowerCase().includes(query) ||
            p.displayName.toLowerCase().includes(query) ||
            p.description?.toLowerCase().includes(query)
        ),
      }))
      .filter((group) => group.permissions.length > 0);
  }, [permissionGroups, searchQuery]);

  // Auto-expand groups with search results
  useEffect(() => {
    if (searchQuery.trim()) {
      setExpandedGroups(new Set(filteredGroups.map((g) => g.name)));
    }
  }, [searchQuery, filteredGroups]);

  const handlePermissionToggle = (permissionName: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(permissionName)) {
        next.delete(permissionName);
      } else {
        next.add(permissionName);
      }
      return next;
    });
  };

  const handleGroupToggle = (groupName: string, permissions: Permission[]) => {
    const groupPermissionNames = permissions.map((p) => p.name);
    const allSelected = groupPermissionNames.every((name) =>
      selectedPermissions.has(name)
    );

    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        // Deselect all
        groupPermissionNames.forEach((name) => next.delete(name));
      } else {
        // Select all
        groupPermissionNames.forEach((name) => next.add(name));
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const allPermissionNames = permissionGroups.flatMap((g) =>
      g.permissions.map((p) => p.name)
    );
    setSelectedPermissions(new Set(allPermissionNames));
  };

  const handleDeselectAll = () => {
    setSelectedPermissions(new Set());
  };

  const handleSave = async () => {
    try {
      await updatePermissionsMutation.mutateAsync({
        roleName: role.name,
        permissions: {
          permissions: permissionGroups.flatMap((group) =>
            group.permissions.map((perm) => ({
              name: perm.name,
              isGranted: selectedPermissions.has(perm.name),
            }))
          ),
        },
      });

      toast.success('Permissions updated successfully', {
        description: `Permissions for "${role.name}" have been updated.`,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
      toast.error('Failed to update permissions', {
        description: message,
      });
    }
  };

  const toggleGroupExpanded = (groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  // Calculate statistics
  const totalPermissions = permissionGroups.reduce(
    (sum, g) => sum + g.permissions.length,
    0
  );
  const selectedCount = selectedPermissions.size;

  if (!allPermissionsData) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <KeenIcon icon="arrows-circle" className="mb-4 animate-spin text-4xl text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading permissions...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Permission Assignment</CardTitle>
              <CardDescription>
                Manage permissions for the "{role.name}" role
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-sm">
                {selectedCount} / {totalPermissions} selected
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <KeenIcon
              icon="magnifier"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="search"
              placeholder="Search permissions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Bulk Actions */}
          {isEditMode && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                className="gap-2"
              >
                <KeenIcon icon="check-square" />
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeselectAll}
                className="gap-2"
              >
                <KeenIcon icon="cross-square" />
                Deselect All
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={updatePermissionsMutation.isPending}
                className="ml-auto gap-2"
              >
                {updatePermissionsMutation.isPending && (
                  <KeenIcon icon="arrows-circle" className="animate-spin" />
                )}
                Save Changes
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permission Groups */}
      {filteredGroups.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <KeenIcon icon="information-circle" className="mb-4 text-4xl text-muted-foreground" />
            <h3 className="mb-2 text-lg font-semibold">No permissions found</h3>
            <p className="text-sm text-muted-foreground">
              {searchQuery
                ? 'Try adjusting your search criteria'
                : 'No permissions available for this role'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredGroups.map((group) => (
            <PermissionGroupCard
              key={group.name}
              group={group}
              selectedPermissions={selectedPermissions}
              isExpanded={expandedGroups.has(group.name)}
              isEditMode={isEditMode}
              onToggleExpanded={() => toggleGroupExpanded(group.name)}
              onPermissionToggle={handlePermissionToggle}
              onGroupToggle={() => handleGroupToggle(group.name, group.permissions)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Permission Group Card Component
 */
interface PermissionGroupCardProps {
  group: PermissionGroup;
  selectedPermissions: Set<string>;
  isExpanded: boolean;
  isEditMode: boolean;
  onToggleExpanded: () => void;
  onPermissionToggle: (permissionName: string) => void;
  onGroupToggle: () => void;
}

function PermissionGroupCard({
  group,
  selectedPermissions,
  isExpanded,
  isEditMode,
  onToggleExpanded,
  onPermissionToggle,
  onGroupToggle,
}: PermissionGroupCardProps) {
  const selectedCount = group.permissions.filter((p) =>
    selectedPermissions.has(p.name)
  ).length;
  const totalCount = group.permissions.length;
  const isFullySelected = selectedCount === totalCount;
  const isPartiallySelected = selectedCount > 0 && !isFullySelected;

  return (
    <Card>
      <Collapsible open={isExpanded} onOpenChange={onToggleExpanded}>
        <div className="flex items-center justify-between p-4">
          <CollapsibleTrigger className="flex flex-1 items-center gap-3 text-left">
            <KeenIcon
              icon={isExpanded ? 'down' : 'right'}
              className="text-muted-foreground transition-transform"
            />
            <div className="flex-1">
              <h3 className="font-semibold">{group.displayName}</h3>
              <p className="text-sm text-muted-foreground">
                {selectedCount} of {totalCount} permission{totalCount !== 1 ? 's' : ''}{' '}
                selected
              </p>
            </div>
          </CollapsibleTrigger>

          {isEditMode && (
            <div className="flex items-center gap-2">
              <Checkbox
                checked={isFullySelected}
                onCheckedChange={onGroupToggle}
                aria-label={`Select all ${group.displayName} permissions`}
                data-indeterminate={isPartiallySelected}
              />
            </div>
          )}
        </div>

        <CollapsibleContent>
          <div className="border-t">
            <div className="divide-y">
              {group.permissions.map((permission) => (
                <div
                  key={permission.name}
                  className="flex items-start gap-3 p-4 hover:bg-muted/50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{permission.displayName}</p>
                      <code className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        {permission.name}
                      </code>
                    </div>
                    {permission.description && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {permission.description}
                      </p>
                    )}
                  </div>

                  {isEditMode ? (
                    <Checkbox
                      checked={selectedPermissions.has(permission.name)}
                      onCheckedChange={() => onPermissionToggle(permission.name)}
                    />
                  ) : (
                    selectedPermissions.has(permission.name) && (
                      <KeenIcon icon="check" className="text-green-600" />
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

/**
 * Format provider name to display name
 */
function formatProviderName(providerName: string): string {
  const displayNames: Record<string, string> = {
    Identity: 'Identity Management',
    User: 'User Management',
    Gamification: 'Gamification & Engagement',
    Content: 'Content Management',
    Notifications: 'Notifications',
    Analytics: 'Analytics & Reporting',
    Admin: 'Admin Operations',
    Wallet: 'Wallet & Coins',
    Communication: 'Communication',
  };

  return displayNames[providerName] || `${providerName} Management`;
}
